// 旅行手配インバウンド事業ページのデータ。
// 設計仕様: docs/superpowers/specs/2026-09-03-inbound-business-page-design.md

/**
 * 画像パスはここに集約する。
 * public/business/ に同名のダミー画像が置いてあるので、
 * 写真が届いたらファイルを上書きするだけで差し替わる。コードは触らなくてよい。
 */
export const IMAGES = {
  hero: '/business/hero.jpg',
  intro: '/business/intro.jpg',
  services: '/business/services.jpg',
  cases: ['/business/case-1.jpg', '/business/case-2.jpg', '/business/case-3.jpg'],
  // 背景リング用。12点を24スロットに2回ずつ配置する。
  ring: Array.from({ length: 12 }, (_, i) =>
    `/business/ring/ring-${String(i + 1).padStart(2, '0')}.jpg`),
};

/** 背景リングの寸法。設計仕様 8.3 の確定値。 */
export const RING = {
  slots: 24,
  radius: 905,
  size: 150,          // 正方形
  cornerRatio: 0.16,  // 角丸を画像サイズに比例させる
  minViewport: 1200,  // これ以下の幅では表示しない
};

/** 縦ラベル（JAPAN / OSAKA）の設定。設計仕様 8.4 の確定値。 */
export const SIDE_LABELS = {
  left: 'JAPAN',
  right: 'OSAKA',
  heightFill: 0.78,   // 画面高のうち文字が占める割合
  upRatio: 0.5,       // 余りのどれだけを上へ寄せるか（0.5=上端）
  touchGap: 0.03,     // 字面どうしが触れないための余裕
  minViewport: 1200,
};

export const CONTACT = {
  tel: '+81 6 6357 3668',
  telHref: 'tel:+81663573668',
  email: 'info@jtc-net.co.jp',
};

/** 手配できること。名称と説明は翻訳キーで持つ。 */
export const SERVICE_KEYS = ['rooms', 'coach', 'incentive', 'meals', 'guide', 'billing'];

/** 対応エリア。都市名は翻訳キーで持つ。 */
export const AREA_KEYS = ['kansai', 'chubu', 'kanto', 'other'];

/** よくあるご質問。回答は未確定のため、設問のみ翻訳キーで持つ。 */
export const FAQ_KEYS = ['minimum', 'quotation', 'payment', 'cancellation', 'language'];

/**
 * 手配事例。実データが未確定のため ◯◯ で伏せている。
 * newsData.js と同じく、1件に全言語をまとめて持つ。
 */
export const CASES = [
  {
    id: 1,
    kind: 'GROUP',
    image: IMAGES.cases[0],
    figure: { ja: '◯◯', en: '◯◯', 'zh-CN': '◯◯', 'zh-TW': '◯◯' },
    unit: {
      ja: '名／◯泊',
      en: 'pax / ◯ nights',
      'zh-CN': '人／◯晚',
      'zh-TW': '人／◯晚',
    },
    text: {
      ja: 'ホテル◯館に分宿。貸切バス◯台を同時手配。',
      en: 'Split across ◯ hotels, with ◯ coaches arranged in parallel.',
      'zh-CN': '分宿于◯家酒店，同时安排◯辆包车。',
      'zh-TW': '分宿於◯家飯店，同時安排◯輛包車。',
    },
  },
  {
    id: 2,
    kind: 'INCENTIVE',
    image: IMAGES.cases[1],
    figure: { ja: '◯◯', en: '◯◯', 'zh-CN': '◯◯', 'zh-TW': '◯◯' },
    unit: { ja: '名', en: 'pax', 'zh-CN': '人', 'zh-TW': '人' },
    text: {
      ja: 'ホテル1館を借り上げ、宴会場を含めて手配。',
      en: 'Whole-hotel buyout including the banquet hall.',
      'zh-CN': '包下整家酒店，含宴会厅一并安排。',
      'zh-TW': '包下整家飯店，含宴會廳一併安排。',
    },
  },
  {
    id: 3,
    kind: 'FIT',
    image: IMAGES.cases[2],
    figure: { ja: '通年', en: 'Year', 'zh-CN': '全年', 'zh-TW': '全年' },
    unit: { ja: '連泊', en: 'round', 'zh-CN': '连住', 'zh-TW': '連住' },
    text: {
      ja: '市内ホテルを通年でブロックし、継続的に送客。',
      en: 'Year-round room block in city hotels with continuous bookings.',
      'zh-CN': '全年包下市内酒店房间，持续送客。',
      'zh-TW': '全年包下市內飯店房間，持續送客。',
    },
  },
];

/**
 * リングのスロットに写真を割り当てる。
 *
 * 単純な巡回（i % n）だと、スロット数が枚数の倍数のときに同じ写真が
 * ちょうど対角へ揃う。24スロット×12枚では半周ごとに同じ並びが現れ、
 * 回転させたとき反復が目に付く。2周目以降を一定量ずらして避ける。
 *
 * そのうえで、円周は端がつながっているので最後と最初の隣接も含めて
 * 同じ写真が隣り合っていないか点検し、見つかれば入れ替える。
 */
export function assignRing(slots, files) {
  const n = files.length;
  const shift = Math.floor(n / 2) + 1;
  const idx = Array.from({ length: slots }, (_, i) => (i + Math.floor(i / n) * shift) % n);

  for (let i = 0; i < slots; i++) {
    const next = (i + 1) % slots;
    if (idx[i] !== idx[next]) continue;
    for (let j = 0; j < slots; j++) {
      const a = idx[j];
      const b = idx[next];
      const jPrev = idx[(j - 1 + slots) % slots];
      const jNext = idx[(j + 1) % slots];
      const nPrev = idx[i];
      const nNext = idx[(next + 1) % slots];
      if (a !== nPrev && a !== nNext && b !== jPrev && b !== jNext) {
        idx[j] = b;
        idx[next] = a;
        break;
      }
    }
  }
  return idx.map((i) => files[i]);
}
